<script setup lang="ts">
import { computed, defineProps, reactive, ref, RenderFunction } from 'vue'
import { openConfirmDialog } from '../../../../utilities/useDialog'
import { i18n } from '../../../../localization'
import { KycDocumentVerificationType } from '../../../../typings'
import { PersonalAccountData } from '../../../../utilities/account/useAccountDataPersonal'
import { LegalAccountData } from '../../../../utilities/account/useAccountDataLegal'
import { FormContext, useForm } from 'vee-validate'
import { CheckIcon, XIcon } from '@heroicons/vue/solid'
import { KycDocument, MinimalKycDocument } from '../../../../utilities/api'
import AccountKycDocumentUpload from './AccountKycDocumentUpload.vue'
import { promiseHandler } from '../../../../utilities/helpers'
import { ApiRes } from '../../../../utilities/api'
import { AxiosError } from 'axios'
import BaseButtonSecondary from '../../../base/BaseButtonSecondary.vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'

const props = defineProps<{
    verificationType: KycDocumentVerificationType
    store: PersonalAccountData | LegalAccountData
    icon: RenderFunction
    showAddButton: boolean
    newKycUploadOnInit: boolean
    hasPendingKycDocuments: boolean
    missingRequiredLegalTypes?: string[]
}>()

const form = useForm()

interface KycForm {
    visible: boolean
    saving: boolean
    kycDocument: MinimalKycDocument
    form: FormContext | null
    uploading: boolean
    isRequired: boolean
}

const saving = ref(false)

const identificationTypes = computed(() => {
    return allIdentificationTypes[props.verificationType]
})

const allIdentificationTypes = {
    kyc_id: [
        {
            key: 'passport',
            label: i18n.global.t('verification_kyc_document.type.passport'),
        },
        {
            key: 'id-card',
            label: i18n.global.t('verification_kyc_document.type.id-card'),
        },
    ],
    kyc_tax: [
        {
            key: 'invoice',
            label: i18n.global.t('verification_kyc_document.type.invoice'),
        },
    ],
    kyc_bank: [
        {
            key: 'bank-statement',
            label: i18n.global.t('verification_kyc_document.type.bank-statement'),
        },
    ],
    kyc_address: [
        {
            key: 'residence-confirmation',
            label: i18n.global.t('verification_kyc_document.type.residence-confirmation'),
        },
    ],
    kyc_legal: [
        {
            key: 'association-register-extract',
            label: i18n.global.t('verification_kyc_document.type.association-register-extract'),
        },
        {
            key: 'list-of-shareholders',
            label: i18n.global.t('verification_kyc_document.type.list-of-shareholders'),
        },
        {
            key: 'articles-of-association',
            label: i18n.global.t('verification_kyc_document.type.articles-of-association'),
        },
        {
            key: 'id-of-beneficial-owners',
            label: i18n.global.t('verification_kyc_document.type.id-of-beneficial-owners'),
        },
        {
            key: 'id-of-managing-director',
            label: i18n.global.t('verification_kyc_document.type.id-of-managing-director'),
        },
        {
            key: 'proof-of-authority-to-represent',
            label: i18n.global.t('verification_kyc_document.type.proof-of-authority-to-represent'),
        },
    ],
}

async function saveKycDocument(kycForm: KycForm) {
    console.log(kycForm)
    const { valid } = await form.validate()
    if (!valid) {
        openConfirmDialog(i18n.global.t('dialog.formInvalid.title'), i18n.global.t('dialog.formInvalid.text'), {
            type: 'Error',
            hideCancel: true,
        })
        return
    }

    kycForm.saving = true
    // if (kycForm.kycDocument.type === 'bank-statement') {
    //     kycForm.kycDocumen['wallet_id'] = sel
    // }

    return new Promise((resolve, reject) => {
        props.store
            .saveKycDocument(kycForm.kycDocument)
            .then((data) => {
                kycForm.saving = false
                kycForm.visible = false
                resolve(data)
            })
            .catch((e) => {
                saving.value = false
                reject(e)
            })
    })
}

async function isValid() {
    const { valid } = await form.validate()
    return valid
}

function emptyDocument(typeKey?: string): MinimalKycDocument {
    let emptyDoc: MinimalKycDocument = {
        verification_type: props.verificationType,
        type: typeKey ?? identificationTypes.value[0].key,
        storage_key: '',
    }
    if (props.verificationType === 'kyc_bank') {
        emptyDoc.bankAccount = {
            iban: '',
            // bic: '', // currently not used
            holder: props.store.getBankDataFromProfile.holder,
        }
    }
    return emptyDoc
}

function newForm(typeKey?: string, isRequired = false): KycForm {
    return {
        visible: true,
        saving: false,
        kycDocument: emptyDocument(typeKey),
        form: null,
        uploading: false,
        isRequired,
    }
}

const uploadForms: reactive<KycForm[]> = reactive([])

if (props.verificationType === 'kyc_legal') {
    const keysToCreate =
        props.hasPendingKycDocuments && (props.missingRequiredLegalTypes?.length ?? 0) > 0
            ? props.missingRequiredLegalTypes
            : !props.hasPendingKycDocuments
            ? allIdentificationTypes.kyc_legal.map((t) => t.key)
            : []

    ;(keysToCreate ?? []).forEach((key) => uploadForms.push(newForm(key, true)))
} else if (props.newKycUploadOnInit && props.verificationType !== 'kyc_external') {
    uploadForms.push(newForm())
}

const visibleUploadForms = computed(() => uploadForms.filter((form) => form.visible))
const hasUploadingForm = computed(() => !!uploadForms.find((form) => form.uploading))

const hasNewKycUpload = computed(() => !!uploadForms.find((kycUpload) => kycUpload.visible))

function identificationTypesForForm(form: KycForm) {
    if (props.verificationType === 'kyc_legal' && form.isRequired) {
        const match = allIdentificationTypes.kyc_legal.find((type) => type.key === form.kycDocument.type)
        return match ? [match] : identificationTypes.value
    }
    return identificationTypes.value
}

async function saveAll() {
    let hasErrors = false
    for (let idx in uploadForms) {
        let kycForm = uploadForms[idx] as KycForm
        if (!kycForm.visible) {
            continue
        }
        const { valid } = await kycForm.form.validate()
        if (valid === false) {
            hasErrors = true
        }
    }

    if (hasErrors) {
        openConfirmDialog(i18n.global.t('dialog.formInvalid.title'), i18n.global.t('dialog.formInvalid.text'), {
            type: 'Error',
            hideCancel: true,
        })
        return
    }

    saving.value = true

    if (props.verificationType === 'kyc_legal') {
        const kycDocuments: KycDocument[] = uploadForms.map((form) => form.kycDocument as KycDocument)

        try {
            await (props.store as any).saveBatchKycDocuments(kycDocuments)

            // Refresh pending documents in parent and hide all upload fields
            if ('fetchKycDocuments' in props.store) {
                ;(props.store as any).fetchKycDocuments()
            }
            if ('fetchBankAccounts' in props.store) {
                ;(props.store as any).fetchBankAccounts()
            }

            uploadForms.forEach((form) => {
                form.visible = false
            })
        } catch (e: any) {
            const errors = e?.response?.data?.errors

            if (Array.isArray(errors)) {
                errors.forEach((err) => {
                    const match = typeof err.field === 'string' ? err.field.match(/^documents\.(\d+)/) : null
                    if (!match) {
                        return
                    }

                    const index = Number(match[1])
                    const kycForm = uploadForms[index] as KycForm | undefined

                    if (kycForm?.form && kycForm.visible) {
                        kycForm.form.setErrors({
                            'newKycDocument.kyc_document': err.message,
                        })
                    }
                })
            }

            saving.value = false
            return
        }

        saving.value = false
        return
    }

    for (let idx in uploadForms) {
        let kycForm = uploadForms[idx] as KycForm
        if (!kycForm.visible) {
            continue
        }
        const [data, error] = await promiseHandler<ApiRes, AxiosError>(saveKycDocument(kycForm))

        if (error) {
            kycForm.form.setErrors({
                'newKycDocument.kyc_document': error.response?.data?.message ?? error.response?.data?.errorMessage,
            })
        } else {
            props.store.fetchBankAccounts()
        }
    }
    saving.value = false
}
</script>

<template>
    <div class="space-y-6">
        <div v-for="(uploadForm, idx) in visibleUploadForms" :key="idx" class="group flex space-x-2">
            <AccountKycDocumentUpload
                v-model="uploadForm.kycDocument"
                v-model:form="uploadForm.form"
                v-model:uploading="uploadForm.uploading"
                :icon="icon"
                :store="store"
                :identification-types="identificationTypesForForm(uploadForm)"
                :verification-type="verificationType"
                class="grow" />
            <button
                v-if="visibleUploadForms.length > 1 && !(verificationType === 'kyc_legal' && uploadForm.isRequired)"
                :title="$t('verification.upload_button_remove_item')"
                class="display grow-0 text-gray-500 hover:text-primary group-hover:block md:hidden"
                @click="uploadForm.visible = false">
                <XIcon class="w-6" />
            </button>
        </div>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div class="order-last sm:order-first">
                <BaseButtonSecondary
                    v-if="hasNewKycUpload"
                    :icon="CheckIcon"
                    :loading="saving"
                    :disabled="saving || hasUploadingForm"
                    class="w-full text-left sm:w-auto"
                    size="lg"
                    @click="saveAll">
                    {{ $t('account.save') }}
                </BaseButtonSecondary>
            </div>
            <div class="flex justify-end">
                <BaseButtonMuted
                    v-if="showAddButton"
                    class="w-full text-center sm:w-auto"
                    size="lg"
                    @click="uploadForms.push(newForm())">
                    {{ $t('verification_kyc_document.add_new_document') }}
                </BaseButtonMuted>
            </div>
        </div>
    </div>
</template>
